import React, { useState, useMemo, useEffect } from 'react';
import { sortBy } from 'lodash';
import { format as formatDate } from 'date-fns';
import { Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { toast } from 'react-toastify';

import firebase, { functions } from '../../firebase';
import { canUpdateProspectPattern, canUpdateInventoryAdvicePrompt } from '../../shared/abilities';
import { urgencyLevels, parseAdvice, countByUrgency, customPromptFields } from '../../shared/models/inventoryAdvice';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import ProgressButton from '../ProgressButton';
import EditButton from '../EditButton';
import ModalButton from '../ModalButton';
import ModelFormModal from './ModelFormModal';

const db = firebase.firestore();

const generateInventoryAdviceOnCall = functions.httpsCallable('generateInventoryAdviceOnCall');

const tabDefs = [
  { key: 'all', label: '全部' },
  { key: 'high', label: '緊急', urgencyKey: 'high' },
  { key: 'medium', label: '中', urgencyKey: 'medium' },
  { key: 'low', label: '低', urgencyKey: 'low' },
  { key: 'done', label: '対応済み', urgencyKey: 'done' },
];

const urgencyBorderColors = {
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',
  success: '#28a745',
};

const ItemCard = ({ item }) => {
  const u = urgencyLevels[item.urgency];
  return (
    <div className="card mb-2" style={{ borderLeft: `4px solid ${urgencyBorderColors[u.color]}` }}>
      <div className="card-body py-2 px-3">
        <div className="d-flex align-items-center mb-1">
          <span className="mr-2">{u.icon}</span>
          <strong>{item.name}</strong>
          <span className={`ml-2 badge badge-${u.color}`}>{u.label}</span>
        </div>
        {item.situation && (
          <div className="small">
            <span className="text-muted mr-1">状況:</span>{item.situation}
          </div>
        )}
        {item.action && (
          <div className="small">
            <span className="text-muted mr-1">対応:</span>{item.action}
          </div>
        )}
        {item.note && (
          <div className="small text-muted" style={{ whiteSpace: 'pre-line' }}>{item.note}</div>
        )}
      </div>
    </div>
  );
};

const AiAdviceBody = ({ advice }) => {
  const parsed = useMemo(() => parseAdvice(advice?.body), [advice?.body]);
  const [tab, setTab] = useState('all');
  useEffect(_ => setTab('all'), [advice?.id]);

  if (!advice) return null;
  const counts = countByUrgency(parsed.items);
  const filteredItems = tab === 'all' ? parsed.items : parsed.items.filter(_ => _.urgency === tab);
  const isStructured = parsed.items.length > 0;

  if (!isStructured) {
    return <div style={{ whiteSpace: 'pre-line' }}>{advice.body}</div>;
  }
  return (
    <div>
      <Nav tabs className="mb-3">
        {tabDefs.map(({ key, label, urgencyKey }) => {
          const count = urgencyKey ? counts[urgencyKey] : parsed.items.length;
          return (
            <NavItem key={key}>
              <NavLink
                active={tab === key}
                onClick={_ => setTab(key)}
                style={{ cursor: 'pointer' }}
              >
                {urgencyKey && <span className="mr-1">{urgencyLevels[urgencyKey].icon}</span>}
                {label}
                <span className="ml-1 badge badge-secondary">{count}</span>
              </NavLink>
            </NavItem>
          );
        })}
      </Nav>
      <TabContent activeTab={tab}>
        <TabPane tabId={tab}>
          {filteredItems.length === 0 ? (
            <div className="text-muted py-3">該当する商品はありません。</div>
          ) : (
            filteredItems.map((item, i) => <ItemCard key={i} item={item} />)
          )}
        </TabPane>
      </TabContent>
      {parsed.preface && (
        <div className="mt-3 small text-muted" style={{ whiteSpace: 'pre-line' }}>{parsed.preface}</div>
      )}
    </div>
  );
};

const UserCommentBody = ({ advice }) => (
  <div className="p-3 bg-light rounded">
    <div className="small text-muted mb-2">
      <span className="fas fa-user mr-1" />
      {advice.createdBy || 'ユーザー'} のコメント
    </div>
    <div style={{ whiteSpace: 'pre-line' }}>{advice.body}</div>
  </div>
);

const HistoryItem = ({ advice, isSelected, onClick }) => {
  const isUser = advice.type === 'user';
  const date = advice.createdAt?.toDate && formatDate(advice.createdAt.toDate(), 'MM/dd HH:mm');
  const summary = isUser ? advice.body : (advice.body || '').replace(/\n/g, ' ');
  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn btn-block text-left p-2 mb-1 ${isSelected ? 'btn-primary' : 'btn-light'}`}
      style={{ borderLeft: isSelected ? '3px solid #007bff' : '3px solid transparent' }}
    >
      <div className="small d-flex align-items-center">
        <span className="mr-1">{isUser ? '👤' : '🤖'}</span>
        <span className={isSelected ? '' : 'text-muted'}>{date}</span>
        <span className="ml-1">{isUser ? (advice.createdBy || 'ユーザー') : 'AI'}</span>
      </div>
      {isUser && summary && (
        <div className="small text-truncate mt-1" style={{ opacity: 0.85 }}>
          "{summary}"
        </div>
      )}
    </button>
  );
};

export default function AiInventoryAdviceModal(props) {
  const { isOpen, onClickClose, historyAdvices, isLoadingHistory, hasMoreHistory, onClickLoadMore, user, tenant } = props;
  const [comment, setComment] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  // NOTE: 分析に使う予想販売係数パターンはテナント設定に持たせる。定期実行(regularlyGenerateInventoryAdvice)も同じ設定を読む
  const prospectPatterns = sortBy(
    useCollectionSubscriptionInTenant(isOpen ? db.collection('prospectPatterns') : null, [isOpen]),
    _ => _.createdAt?.toDate?.() || 0
  );
  // 未設定・指定パターン削除済みの場合はfunctions側と同じく作成順の先頭にフォールバック
  const currentProspectPattern = prospectPatterns.find(_ => _.id === tenant?.inventoryAdviceProspectPatternId) ?? prospectPatterns[0];
  const onChangeProspectPattern = async (prospectPatternId) => {
    try {
      await tenant.ref.update({ inventoryAdviceProspectPatternId: prospectPatternId });
      const name = prospectPatterns.find(_ => _.id === prospectPatternId)?.name;
      toast.success(`係数パターンを「${name}」に変更しました`);
    } catch (e) {
      console.error(e);
      toast.error('係数パターンの保存に失敗しました');
    }
  };

  const sortedHistory = useMemo(() => {
    return [...(historyAdvices || [])].sort((a, b) => {
      const at = a.createdAt?.toDate?.()?.getTime() || 0;
      const bt = b.createdAt?.toDate?.()?.getTime() || 0;
      return bt - at;
    });
  }, [historyAdvices]);

  const latestAiAdvice = sortedHistory.find(_ => _.type === 'ai');

  useEffect(() => {
    if (selectedId == null && latestAiAdvice) {
      setSelectedId(latestAiAdvice.id);
    }
  }, [latestAiAdvice?.id]);

  const selectedAdvice = sortedHistory.find(_ => _.id === selectedId) || latestAiAdvice;

  const onClickAnalyze = async () => {
    try {
      await generateInventoryAdviceOnCall({
        userComment: comment || undefined,
        userName: user.displayName,
      });
      setComment('');
      setSelectedId(null);
    } catch (e) {
      console.error(e);
      toast.error('分析に失敗しました');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClickClose} style={{ width: '90vw', maxWidth: '90vw' }}>
      <ModalHeader toggle={onClickClose}>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <span>
            AI在庫アドバイス
            {selectedAdvice?.createdAt?.toDate && (
              <small className="ml-2 text-muted">
                {formatDate(selectedAdvice.createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                {selectedAdvice.type === 'user' && ` ・ ${selectedAdvice.createdBy || 'ユーザー'} コメント`}
              </small>
            )}
          </span>
          {prospectPatterns.length > 0 && (
            <span className="d-flex align-items-center ml-md-3">
              <span className="small text-muted mr-2" style={{ whiteSpace: 'nowrap' }}>係数パターン</span>
              <select
                className="form-control form-control-sm"
                style={{ width: 'auto' }}
                value={currentProspectPattern?.id || ''}
                onChange={_ => onChangeProspectPattern(_.target.value)}
                disabled={!canUpdateProspectPattern(user)}
              >
                {prospectPatterns.map(_ => (
                  <option key={_.id} value={_.id}>{_.name}</option>
                ))}
              </select>
            </span>
          )}
          <ModalButton
            size="sm"
            className="ml-md-2"
            title="前提条件"
            content={_ => (
              <div>
                <div className="d-flex justify-content-end mb-2">
                  <EditButton size="sm" itemRef={tenant.ref} FormModal={ModelFormModal} formProps={{ title: '前提条件', fields: customPromptFields() }} disabled={!canUpdateInventoryAdvicePrompt(user)} />
                </div>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {tenant.inventoryAdviceCustomPrompt || <span className="text-muted">未設定</span>}
                </div>
              </div>
            )}
          >
            前提条件
          </ModalButton>
        </div>
      </ModalHeader>
      <ModalBody style={{ minHeight: '60vh', maxHeight: '80vh', overflowY: 'auto' }}>
        <div className="d-md-none mb-3">
          <label className="small text-muted mb-1">履歴</label>
          {isLoadingHistory ? (
            <div className="text-muted small">
              <span className="fas fa-spin fa-spinner mr-1" />読み込み中...
            </div>
          ) : sortedHistory.length === 0 ? (
            <div className="text-muted small">履歴がありません</div>
          ) : (
            <>
              <select
                className="form-control"
                value={selectedAdvice?.id || ''}
                onChange={_ => setSelectedId(_.target.value)}
              >
                {sortedHistory.map((_, i) => {
                  const date = _.createdAt?.toDate && formatDate(_.createdAt.toDate(), 'MM/dd HH:mm');
                  const isUser = _.type === 'user';
                  const icon = isUser ? '👤' : '🤖';
                  const who = isUser ? (_.createdBy || 'ユーザー') : 'AI';
                  const preview = isUser ? ` "${(_.body || '').replace(/\n/g, ' ').slice(0, 30)}"` : '';
                  const latestMark = i === 0 ? ' (最新)' : '';
                  return (
                    <option key={_.id} value={_.id}>
                      {icon} {date} {who}{preview}{latestMark}
                    </option>
                  );
                })}
              </select>
              {hasMoreHistory && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary btn-block mt-2"
                  onClick={onClickLoadMore}
                >
                  <span className="fas fa-chevron-down mr-1" />
                  もっと見る
                </button>
              )}
            </>
          )}
        </div>
        <div className="row">
          <div className="col-md-3 mb-3 mb-md-0 d-none d-md-block">
            <div className="small text-muted mb-2">履歴</div>
            {isLoadingHistory ? (
              <div className="text-center py-3 text-muted small">
                <span className="fas fa-spin fa-spinner mr-1" />読み込み中...
              </div>
            ) : sortedHistory.length === 0 ? (
              <div className="text-muted small">履歴がありません</div>
            ) : (
              <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {sortedHistory.map(_ => (
                  <HistoryItem
                    key={_.id}
                    advice={_}
                    isSelected={_.id === selectedAdvice?.id}
                    onClick={() => setSelectedId(_.id)}
                  />
                ))}
                {hasMoreHistory && (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary btn-block mt-2"
                    onClick={onClickLoadMore}
                  >
                    <span className="fas fa-chevron-down mr-1" />
                    もっと見る
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="col-md-9">
            {isLoadingHistory ? (
              <div className="text-center py-5">
                <span className="fas fa-spin fa-spinner fa-2x text-muted" />
                <div className="mt-2 text-muted small">読み込み中...</div>
              </div>
            ) : selectedAdvice == null ? (
              <div className="text-muted">まだアドバイスがありません。再分析してください。</div>
            ) : selectedAdvice.type === 'user' ? (
              <UserCommentBody advice={selectedAdvice} />
            ) : (
              <AiAdviceBody advice={selectedAdvice} />
            )}
          </div>
        </div>
        <hr />
        <div>
          <label className="small text-muted mb-1">コメント（任意・複数行可）</label>
          <textarea
            className="form-control mb-2"
            rows={4}
            placeholder="気になる商品や追加で考慮してほしい事情などを記入してください..."
            value={comment}
            onChange={_ => setComment(_.target.value)}
          />
          <div className="d-flex justify-content-end">
            <ProgressButton color="primary" process={onClickAnalyze}>
              <span className="fas fa-sync-alt mr-1" />
              {comment ? 'コメントして再分析' : '再分析'}
            </ProgressButton>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}
